import HttpService from "./httpService";

export interface WayuvegaCarrier {
    carrierPartnerAccountCode: string;
    carrierPartnerPublicName: string;
    zoneName: string;
    basePrice: number;
    taxPercentage: number;
    taxAmount: number;
    totalAmount: number;
    currency: string;
    minTatDays: number | null;
    maxTatDays: number | null;
    chargeableWeightGrams: number;
}

export interface WayuvegaServiceabilityResult {
    pickupPincode: string;
    dropPincode: string;
    paymentMode: "COD" | "PREPAID";
    carriers: WayuvegaCarrier[];
}

export class AdminOrderService {


    static getOrderById = async (orderId: string) => {
        return HttpService.get<any>(`/orders/${orderId}`);
    }

    static getOrderStats = (params?: Record<string, string>) => {
        return HttpService.get<any>('/orders/stats', { params });
    }

    static getDailyStats = (params?: Record<string, string>) => {
        return HttpService.get<any[]>('/orders/stats/daily', { params });
    }

    static getPendingPaymentOrders = () => {
        return HttpService.get('/order/pending-payments');
    }

    static getAdminOrders = (status: string, params?: {
        page?: number;
        limit?: number;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
    }) => {
        let url = '/orders';
        const queryParams = new URLSearchParams();

        if (status !== 'ALL') {
            queryParams.append('status', status);
        }

        if (params) {
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.limit) queryParams.append('limit', params.limit.toString());
            if (params.search) queryParams.append('search', params.search);
            if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
            if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        }

        const queryString = queryParams.toString();
        if (queryString) {
            url += '?' + queryString;
        }

        return HttpService.get<{
            orders: any[];
            totalCount: number;
            totalPages: number;
            currentPage: number;
            hasNextPage: boolean;
            hasPrevPage: boolean;
        }>(url);
    }

    static verifyPayment = async (orderId: string) => {
        return HttpService.post(`/orders/${orderId}/verify-payment`, {});
    }

    static verifyAdvancePayment = async (orderId: string) => {
        return HttpService.post(`/orders/${orderId}/verify-advance-payment`, {});
    }

    static updateOrderStatus = async (
        orderId: string,
        status: string,
        shippingDetails?: {
            trackingId?: string;
            trackingLink?: string;
            expectedDeliveryDate?: string;
            shippingMethod?: 'manual' | 'partner';
            shippingPartner?: string;
        }
    ) => {
        const payload: any = { status };

        // Add shipping details if provided and status is SHIPPED
        if (status === 'SHIPPED' && shippingDetails) {
            if (shippingDetails.trackingId) {
                payload.trackingId = shippingDetails.trackingId;
            }
            if (shippingDetails.trackingLink) {
                payload.trackingLink = shippingDetails.trackingLink;
            }
            if (shippingDetails.expectedDeliveryDate) {
                payload.expectedDeliveryDate = shippingDetails.expectedDeliveryDate;
            }
            if (shippingDetails.shippingMethod) {
                payload.shippingMethod = shippingDetails.shippingMethod;
            }
            if (shippingDetails.shippingPartner) {
                payload.shippingPartner = shippingDetails.shippingPartner;
            }
        }

        return HttpService.post(`/orders/${orderId}/status`, payload);
    }



    static getAdminOrderItems = async (status?: string) => {
        return HttpService.get<any[]>(`/order-items?status=${status}`)
    }


    static getRecentOrders = async () => {
        return HttpService.get<any[]>(`/orders/recent`)
    }

    static updateExpectedDeliveryDate = async (orderId: string, expectedDeliveryDate: string) => {
        return HttpService.post(`/orders/${orderId}/expected-delivery-date`, { expectedDeliveryDate });
    }

    static updateDeliveryInfo = async (orderId: string, updates: {
        trackingId?: string;
        trackingLink?: string;
        expectedDeliveryDate?: string | null;
    }) => {
        return HttpService.post(`/orders/${orderId}/delivery-info`, updates);
    }

    static getWayuvegaServiceability = async (orderId: string, dimensions?: {
        weight?: number;
        height?: number;
        width?: number;
        length?: number;
    }) => {
        return HttpService.post<WayuvegaServiceabilityResult>(`/orders/${orderId}/wayuvega-serviceability`, dimensions || {});
    }

    // New method specifically for shipping with partners
    static shipWithPartner = async (orderId: string, shippingPartnerId: string, dimensions?: {
        weight?: number;
        height?: number;
        width?: number;
        length?: number;
    }, wayuvegaCarrier?: {
        carrierPartnerPublicName?: string;
        carrierPartnerAccountCode?: string;
    }, pickupLocationName?: string) => {
        const isWayuvega = shippingPartnerId === "WAYUVEGA";

        return HttpService.post(`/orders/${orderId}/ship-with-partner`, {
            partnerId: shippingPartnerId,
            ...(isWayuvega && { partnerType: "WAYUVEGA" }),
            ...dimensions,
            ...(wayuvegaCarrier?.carrierPartnerPublicName && { carrierPartnerPublicName: wayuvegaCarrier.carrierPartnerPublicName }),
            ...(wayuvegaCarrier?.carrierPartnerAccountCode && { carrierPartnerAccountCode: wayuvegaCarrier.carrierPartnerAccountCode }),
            ...(pickupLocationName && { pickupLocationName })
        });
    }

    // Alternative: Enhanced updateOrderStatus for partner shipping
    static collectRemaining = async (orderId: string) => {
        return HttpService.post(`/orders/${orderId}/collect-remaining`, {});
    }

    static shipOrder = async (
        orderId: string,
        shippingDetails: {
            trackingId?: string;
            trackingLink?: string;
            expectedDeliveryDate?: string;
            shippingMethod?: 'manual' | 'partner';
            shippingPartner?: string;
            weight?: number;
            height?: number;
            width?: number;
            length?: number;
            carrierPartnerPublicName?: string;
            carrierPartnerAccountCode?: string;
            pickupLocationName?: string;
        }
    ) => {
        // Use existing API for manual shipping
        if (shippingDetails.shippingMethod === 'manual') {
            return this.updateOrderStatus(orderId, 'SHIPPED', shippingDetails);
        }

        // Use new API for partner shipping
        if (shippingDetails.shippingMethod === 'partner' && shippingDetails.shippingPartner) {
            return this.shipWithPartner(orderId, shippingDetails.shippingPartner, {
                weight: shippingDetails.weight,
                height: shippingDetails.height,
                width: shippingDetails.width,
                length: shippingDetails.length
            }, {
                carrierPartnerPublicName: shippingDetails.carrierPartnerPublicName,
                carrierPartnerAccountCode: shippingDetails.carrierPartnerAccountCode
            }, shippingDetails.pickupLocationName);
        }

        throw new Error('Invalid shipping details provided');
    }

}
